/**
 * `data-screen-label="Agent"`, tab `channels` — **Blocked numbers**, under Caller protection (plan-029 WAPP-PROT;
 * design §40.5, §40.13.2, §40.19.2, DD29-2; D-6; ADR 0081, proposed).
 *
 * ```
 * Blocked numbers
 *   Ends in …123   "repeated hang-ups"   [Every agent in this workspace]   Until removed          [Remove]
 *   Ends in …456                         [This agent]                      Until 3 Nov 2026 14:02 [Remove]
 *   [Show more]
 *   Block a number: [+30 …]  Note [……]  Blocked for (This agent | Every agent in this workspace)  How long [▾]  [Block]
 *   You can block up to 5,000 numbers in a workspace.
 * ```
 *
 * The entries are those that apply to this VoiceAgent — its own and the workspace-wide ones, which carry the "Every
 * agent in this workspace" tag. NEVER A FULL NUMBER: an entry is its last three digits (or "a caller on your own phone
 * system" for a trunk caller blocked from a conversation), its note and its scope; a typed number is sent once and the
 * field is cleared as soon as the api answers. Adding and removing need `voice_agent.write`; while the api's switch is
 * off the page says "not available yet" and offers no form.
 */
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { Button, Icon, Segmented, Tag } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { apiErrorText } from "../../../../api/api-error.js";
import { useGateway } from "../../../../api/provider.js";
import {
  BLOCKED_NUMBER_EXPIRY_HOUR,
  BLOCKED_NUMBER_QUERY_KEY,
  type BlockedNumberAddedView,
  type BlockedNumberExpiryHour,
  type BlockedNumberScope,
  type BlockedNumberView,
} from "../../../../api/slice/blocked-number.js";
import { PartyRefusedError } from "../../../../api/slice/caller-protection.js";
import { portalFormat, portalText, usePortalTranslation } from "../../../../i18n/portal.js";
import { GLYPH } from "../../../../shell/glyph.js";
import { useMemberCan } from "../../../../shell/member-capability.js";

export interface BlockedNumberPanelProps {
  readonly voiceAgentId: string;
}

/** The example a typed number follows (a fictional Greek mobile range). */
export const BLOCKED_NUMBER_EXAMPLE = "+30 690 000 0000";

const EXPIRY_KEY = {
  24: "blocked.expiry_day",
  168: "blocked.expiry_week",
  720: "blocked.expiry_month",
} as const satisfies Record<BlockedNumberExpiryHour, string>;

/** The entry's identity in words: "Ends in …123", or a trunk caller known only by what the trunk sent. */
export function blockedNumberNameOf(entry: BlockedNumberView): string {
  return entry.lastThreeDigit === null
    ? portalText("callerProtection", "blocked.trunk_caller")
    : portalText("callerProtection", "blocked.ending", { last_three: entry.lastThreeDigit });
}

/** A refused add in the portal's language, from its code and rule. */
export function blockedNumberRefusalTextOf(error: unknown): string {
  if (error instanceof PartyRefusedError) {
    if (error.detailCode === "number_invalid") {
      return portalText("callerProtection", "blocked.refused.number_invalid", { example: BLOCKED_NUMBER_EXAMPLE });
    }
    if (error.detailCode === "label_too_long") return portalText("callerProtection", "blocked.refused.label_too_long");
  }
  if (error instanceof ApiError) {
    if (error.code === "blocked_number_limit_reached") return portalText("callerProtection", "blocked.refused.limit");
    if (error.code === "caller_protection_unavailable") return portalText("callerProtection", "blocked.refused.unavailable");
    if (error.code === "block_unavailable") return portalText("callerProtection", "blocked.refused.block_unavailable");
  }
  return apiErrorText(error, portalText("callerProtection", "blocked.refused.other"));
}

/** What an add answers, in words: blocked now, or already blocked in that scope. */
export function blockedNumberAddedTextOf(added: BlockedNumberAddedView): string {
  const lastThree = added.entry.lastThreeDigit ?? "";
  return added.created
    ? portalText("callerProtection", "blocked.added", { last_three: lastThree })
    : portalText("callerProtection", "blocked.already", { last_three: lastThree });
}

interface AddDraft {
  readonly number: string;
  readonly label: string;
  readonly scope: BlockedNumberScope;
  readonly expiryHour: BlockedNumberExpiryHour | null;
}

const EMPTY_DRAFT: AddDraft = { number: "", label: "", scope: "one_voice_agent", expiryHour: null };

export function BlockedNumberPanel({ voiceAgentId }: BlockedNumberPanelProps): ReactNode {
  const { locale } = usePortalTranslation("callerProtection");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const canEdit = useMemberCan().can("voice_agent.write");
  const [draft, setDraft] = useState<AddDraft>(EMPTY_DRAFT);
  const [line, setLine] = useState<{ readonly text: string; readonly refused: boolean } | null>(null);

  const page = useInfiniteQuery({
    queryKey: BLOCKED_NUMBER_QUERY_KEY.forVoiceAgent(voiceAgentId),
    queryFn: ({ pageParam }) => gateway.listBlockedNumbers(voiceAgentId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: BLOCKED_NUMBER_QUERY_KEY.every });
  };

  const add = useMutation({
    mutationFn: (input: AddDraft) =>
      gateway.addBlockedNumber({
        number: input.number.trim(),
        region: null,
        label: input.label.trim() === "" ? null : input.label.trim(),
        scope: input.scope,
        voiceAgentId: input.scope === "one_voice_agent" ? voiceAgentId : null,
        expiryHour: input.expiryHour,
      }),
    onSuccess: async (added, input) => {
      // The typed number leaves the page as soon as the api has it.
      setDraft({ ...EMPTY_DRAFT, scope: input.scope, expiryHour: input.expiryHour });
      setLine({ text: blockedNumberAddedTextOf(added), refused: false });
      await refresh();
    },
    onError: (error) => {
      setLine({ text: blockedNumberRefusalTextOf(error), refused: true });
    },
  });

  const remove = useMutation({
    mutationFn: (partyBlockEntryId: string) => gateway.removeBlockedNumber(partyBlockEntryId),
    onSuccess: async () => {
      setLine({ text: portalText("callerProtection", "blocked.removed"), refused: false });
      await refresh();
    },
    onError: (error) => {
      setLine({ text: apiErrorText(error, portalText("callerProtection", "blocked.refused.remove")), refused: true });
    },
  });

  const heading = <h3 className="ch-card-heading">{portalText("callerProtection", "blocked.title")}</h3>;

  if (page.isError) {
    return (
      <div className="ch-card cp cp-blocked">
        {heading}
        <p className="c-note c-note-critical" role="alert">
          {portalText("callerProtection", "blocked.read_failed")}{" "}
          {apiErrorText(page.error, portalText("callerProtection", "panel.reload"))}
        </p>
      </div>
    );
  }
  if (page.data === undefined) {
    return (
      <div className="ch-card cp cp-blocked">
        {heading}
        <p className="c-note">{portalText("callerProtection", "blocked.reading")}</p>
      </div>
    );
  }

  const available = page.data.pages[0]?.available ?? false;
  const entry = page.data.pages.flatMap((one) => one.entry);
  const format = portalFormat(locale);

  return (
    <div className="ch-card cp cp-blocked" data-blocked-number={available ? "available" : "unavailable"}>
      <div className="cp-head">
        {heading}
        {available ? null : <Tag tone="neutral">{portalText("callerProtection", "panel.unavailable")}</Tag>}
      </div>
      <p className="ch-card-note cp-dek">{portalText("callerProtection", "blocked.dek")}</p>

      {entry.length === 0 ? (
        <p className="ch-card-note">{available ? portalText("callerProtection", "blocked.empty") : portalText("callerProtection", "blocked.unavailable")}</p>
      ) : (
        <ul className="cp-blocked-rows">
          {entry.map((one) => (
            <li className="cp-blocked-row" key={one.partyBlockEntryId} data-scope={one.scope}>
              <span className="cp-blocked-body">
                <span className="cp-blocked-name">
                  <Icon glyph={GLYPH.phoneCall} size="sm" />
                  <span className="cp-blocked-digits">{blockedNumberNameOf(one)}</span>
                  {one.scope === "every_voice_agent" ? (
                    <Tag tone="accent">{portalText("callerProtection", "blocked.scope_every")}</Tag>
                  ) : (
                    <Tag tone="neutral">{portalText("callerProtection", "blocked.scope_this")}</Tag>
                  )}
                </span>
                {one.label === null ? null : <span className="cp-blocked-label">{one.label}</span>}
                <span className="cp-blocked-meta">
                  {one.expiresAt === null
                    ? portalText("callerProtection", "blocked.expiry_never")
                    : portalText("callerProtection", "blocked.expires_on", {
                        date: format.date(one.expiresAt, { dateStyle: "medium", timeStyle: "short" }),
                      })}
                  {one.sourceConversationId === null ? null : ` · ${portalText("callerProtection", "blocked.from_conversation")}`}
                </span>
              </span>
              {canEdit && available ? (
                <Button
                  variant="secondary"
                  disabled={remove.isPending}
                  aria-label={portalText("callerProtection", "blocked.remove_named", { last_three: one.lastThreeDigit ?? "" })}
                  onClick={() => {
                    setLine(null);
                    remove.mutate(one.partyBlockEntryId);
                  }}
                >
                  {portalText("callerProtection", "blocked.remove")}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {page.hasNextPage ? (
        <Button
          variant="ghost"
          disabled={page.isFetchingNextPage}
          onClick={() => {
            void page.fetchNextPage();
          }}
        >
          {portalText("callerProtection", "blocked.more")}
        </Button>
      ) : null}

      {canEdit && available ? (
        <form
          className="cp-blocked-add"
          onSubmit={(event) => {
            event.preventDefault();
            setLine(null);
            add.mutate(draft);
          }}
        >
          <p className="ch-card-label ch-card-label-flush">{portalText("callerProtection", "blocked.add")}</p>
          <div className="cp-blocked-fields">
            <div className="field">
              <label htmlFor={`cp-block-number-${voiceAgentId}`}>{portalText("callerProtection", "blocked.number_label")}</label>
              <input
                id={`cp-block-number-${voiceAgentId}`}
                className="input"
                type="tel"
                inputMode="tel"
                autoComplete="off"
                placeholder={BLOCKED_NUMBER_EXAMPLE}
                value={draft.number}
                onChange={(event) => {
                  setLine(null);
                  setDraft({ ...draft, number: event.currentTarget.value });
                }}
              />
              <span className="c-answer-note">
                {portalText("callerProtection", "blocked.number_hint", { example: BLOCKED_NUMBER_EXAMPLE })}
              </span>
            </div>
            <div className="field">
              <label htmlFor={`cp-block-label-${voiceAgentId}`}>{portalText("callerProtection", "blocked.label_label")}</label>
              <input
                id={`cp-block-label-${voiceAgentId}`}
                className="input"
                type="text"
                maxLength={80}
                placeholder={portalText("callerProtection", "blocked.label_hint")}
                value={draft.label}
                onChange={(event) => {
                  setDraft({ ...draft, label: event.currentTarget.value });
                }}
              />
            </div>
          </div>
          <div className="cp-blocked-fields">
            <div className="field">
              <span className="cp-field-label">{portalText("callerProtection", "blocked.scope_label")}</span>
              <Segmented
                name={`cp-block-scope-${voiceAgentId}`}
                legend={portalText("callerProtection", "blocked.scope_label")}
                options={[
                  { value: "one_voice_agent", label: portalText("callerProtection", "blocked.scope_this") },
                  { value: "every_voice_agent", label: portalText("callerProtection", "blocked.scope_every") },
                ]}
                value={draft.scope}
                onChange={(scope) => {
                  setDraft({ ...draft, scope });
                }}
              />
            </div>
            <div className="field">
              <label htmlFor={`cp-block-expiry-${voiceAgentId}`}>{portalText("callerProtection", "blocked.expiry_label")}</label>
              <span className="c-answer-select cp-select">
                <select
                  id={`cp-block-expiry-${voiceAgentId}`}
                  className="input"
                  value={draft.expiryHour === null ? "" : String(draft.expiryHour)}
                  onChange={(event) => {
                    const raw = event.currentTarget.value;
                    const hour = BLOCKED_NUMBER_EXPIRY_HOUR.find((one) => String(one) === raw);
                    setDraft({ ...draft, expiryHour: hour ?? null });
                  }}
                >
                  <option value="">{portalText("callerProtection", "blocked.expiry_never")}</option>
                  {BLOCKED_NUMBER_EXPIRY_HOUR.map((hour) => (
                    <option key={hour} value={String(hour)}>
                      {portalText("callerProtection", EXPIRY_KEY[hour])}
                    </option>
                  ))}
                </select>
                <Icon glyph={GLYPH.caretDown} size="sm" />
              </span>
            </div>
          </div>
          <div className="ch-card-actions">
            <Button variant="primary" type="submit" disabled={add.isPending || draft.number.trim() === ""}>
              {add.isPending ? portalText("callerProtection", "blocked.adding") : portalText("callerProtection", "blocked.add")}
            </Button>
          </div>
        </form>
      ) : null}

      {line === null ? null : (
        <p className={line.refused ? "c-note c-note-critical" : "ch-note"} role={line.refused ? "alert" : "status"}>
          {line.text}
        </p>
      )}
      <p className="ch-card-foot">{portalText("callerProtection", "blocked.limit")}</p>
    </div>
  );
}
